import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useGetMsbContractQuery } from "../api/msb-contract";
import type { MsbCompletionAct } from "../types/msb";
import type { CalculationsSummary } from "../types";

export function useMsbCalculationsPage() {
  const { t, i18n } = useTranslation("EmployeePortal");
  const locale = (i18n.language as "ru" | "kk" | "en") || "ru";
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const { data: contract, isLoading, isError, error } = useGetMsbContractQuery();

  const summary: CalculationsSummary = useMemo(() => {
    if (!contract) {
      return {
        contractAmount: 0,
        totalPaid: 0,
        totalApproved: 0,
        totalPending: 0,
        remaining: 0,
        paidPercent: 0,
        approvedPercent: 0,
      };
    }

    const contractAmount = parseFloat(contract.contract_amount || "0");
    let totalPaid = 0;
    let totalApproved = 0;
    let totalPending = 0;

    for (const act of contract.completion_acts) {
      const amount = parseFloat(act.amount || "0");
      if (act.is_paid) {
        totalPaid += amount;
      } else if (act.is_payment_approved || act.status === "approved" || act.status === "signed") {
        totalApproved += amount;
      } else if (
        act.status === "draft" ||
        act.status === "pending_review" ||
        act.status === "pending_signing" ||
        act.status === "signing_pending"
      ) {
        totalPending += amount;
      }
    }

    const remaining = Math.max(contractAmount - totalPaid - totalApproved - totalPending, 0);
    const paidPercent = contractAmount > 0 ? (totalPaid / contractAmount) * 100 : 0;
    const approvedPercent = contractAmount > 0 ? (totalApproved / contractAmount) * 100 : 0;

    return { contractAmount, totalPaid, totalApproved, totalPending, remaining, paidPercent, approvedPercent };
  }, [contract]);

  const sortedActs: MsbCompletionAct[] = useMemo(() => {
    if (!contract) return [];
    return [...contract.completion_acts].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );
  }, [contract]);

  const isNotFound = isError && error && "status" in error && (error.status === 404 || error.status === 403);

  return {
    contract,
    isLoading,
    isError,
    isNotFound,
    summary,
    sortedActs,
    createModalOpen,
    setCreateModalOpen,
    t,
    locale,
  };
}
